import React, { useState, useEffect } from 'react';
import { api } from '../App';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Progress } from '../components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import {
  Plus,
  Edit,
  Trash2,
  RefreshCw,
  DollarSign,
  Home,
  TrendingUp,
  CheckCircle,
  XCircle,
  AlertCircle
} from 'lucide-react';

const PROPERTY_TYPES = ['SFR', '2-4 Unit', '5+ Unit', 'Mixed Use', 'Commercial'];
const LOAN_TYPES = ['DSCR', 'Fix & Flip', 'Ground Up', 'Bridge', 'Conventional', 'FHA', 'VA'];
const CITIZENSHIP_TYPES = ['US Citizen', 'Permanent Resident', 'Foreign National'];
const LOAN_STATUSES = ['active', 'closed', 'cancelled'];

const ClientLoansSection = ({ contactId, contact }) => {
  const [loans, setLoans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lendingScore, setLendingScore] = useState(null);
  const [showLoanDialog, setShowLoanDialog] = useState(false);
  const [showMatchDialog, setShowMatchDialog] = useState(false);
  const [editingLoan, setEditingLoan] = useState(null);
  const [matchResults, setMatchResults] = useState(null);
  const [matchingLoanId, setMatchingLoanId] = useState(null);

  const [loanForm, setLoanForm] = useState({
    loan_type: '',
    loan_amount: '',
    down_payment_percent: '',
    down_payment_amount: '',
    property_address: '',
    property_type: '',
    purchase_price: '',
    estimated_rent: '',
    credit_score: '',
    citizenship: '',
    timeline: '',
    notes: '',
    status: 'active'
  });

  useEffect(() => {
    fetchLoans();
    fetchLendingScore();
  }, [contactId]);

  const fetchLoans = async () => {
    try {
      const response = await api.get(`/contacts/${contactId}/loans`);
      setLoans(response.data);
    } catch (error) {
      console.error('Failed to load loans:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLendingScore = async () => {
    try {
      const response = await api.get(`/contacts/${contactId}/lending-score`);
      setLendingScore(response.data);
    } catch (error) {
      console.error('Failed to load lending score:', error);
    }
  };

  const resetForm = () => {
    setLoanForm({
      loan_type: '',
      loan_amount: '',
      down_payment_percent: '',
      down_payment_amount: '',
      property_address: '',
      property_type: '',
      purchase_price: '',
      estimated_rent: '',
      credit_score: contact?.credit_score || '',
      citizenship: '',
      timeline: '',
      notes: '',
      status: 'active'
    });
    setEditingLoan(null);
  };

  const openCreateDialog = () => {
    resetForm();
    setShowLoanDialog(true);
  };

  const openEditDialog = (loan) => {
    setEditingLoan(loan);
    setLoanForm({
      loan_type: loan.loan_type || '',
      loan_amount: loan.loan_amount || '',
      down_payment_percent: loan.down_payment_percent || '',
      down_payment_amount: loan.down_payment_amount || '',
      property_address: loan.property_address || '',
      property_type: loan.property_type || '',
      purchase_price: loan.purchase_price || '',
      estimated_rent: loan.estimated_rent || '',
      credit_score: loan.credit_score || '',
      citizenship: loan.citizenship || '',
      timeline: loan.timeline || '',
      notes: loan.notes || '',
      status: loan.status || 'active'
    });
    setShowLoanDialog(true);
  };

  const handleSubmit = async () => {
    if (!loanForm.loan_type) {
      toast.error('Loan type is required');
      return;
    }

    const payload = {
      contact_id: contactId,
      ...loanForm,
      loan_amount: loanForm.loan_amount ? parseFloat(loanForm.loan_amount) : null,
      down_payment_percent: loanForm.down_payment_percent ? parseFloat(loanForm.down_payment_percent) : null,
      down_payment_amount: loanForm.down_payment_amount ? parseFloat(loanForm.down_payment_amount) : null,
      purchase_price: loanForm.purchase_price ? parseFloat(loanForm.purchase_price) : null,
      estimated_rent: loanForm.estimated_rent ? parseFloat(loanForm.estimated_rent) : null,
      credit_score: loanForm.credit_score ? parseInt(loanForm.credit_score) : null
    };

    try {
      if (editingLoan) {
        await api.put(`/contacts/${contactId}/loans/${editingLoan.loan_id}`, payload);
        toast.success('Loan updated');
      } else {
        await api.post(`/contacts/${contactId}/loans`, payload);
        toast.success('Loan added');
      }
      setShowLoanDialog(false);
      resetForm();
      fetchLoans();
      fetchLendingScore();
    } catch (error) {
      toast.error('Failed to save loan');
    }
  };

  const handleDelete = async (loanId) => {
    if (!window.confirm('Are you sure you want to delete this loan?')) return;

    try {
      await api.delete(`/contacts/${contactId}/loans/${loanId}`);
      toast.success('Loan deleted');
      fetchLoans();
      fetchLendingScore();
    } catch (error) {
      toast.error('Failed to delete loan');
    }
  };

  const handleMatchPrograms = async (loanId) => {
    setMatchingLoanId(loanId);
    try {
      const response = await api.post(`/contacts/${contactId}/loans/${loanId}/match-programs`);
      setMatchResults(response.data);
      setShowMatchDialog(true);
      fetchLoans(); // Refresh to get updated lending score
    } catch (error) {
      toast.error('Failed to match programs');
    } finally {
      setMatchingLoanId(null);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    if (score >= 40) return 'text-orange-600';
    return 'text-red-600';
  };

  const getScoreBg = (score) => {
    if (score >= 80) return 'bg-green-500';
    if (score >= 60) return 'bg-yellow-500';
    if (score >= 40) return 'bg-orange-500';
    return 'bg-red-500';
  };

  return (
    <div className="space-y-6">
      {/* Lending Score Card */}
      <Card data-testid="lending-score-card">
        <CardHeader className="pb-3">
          <div className="flex justify-between items-start">
            <div>
              <CardTitle className="text-lg">Lending Score</CardTitle>
              <CardDescription>Overall qualification score</CardDescription>
            </div>
            <Button variant="ghost" size="sm" onClick={fetchLendingScore}>
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {lendingScore ? (
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div className={`text-4xl font-bold ${getScoreColor(lendingScore.score)}`}>
                  {lendingScore.score.toFixed(0)}%
                </div>
                <div className="flex-1">
                  <Progress 
                    value={lendingScore.score} 
                    className="h-3"
                  />
                </div>
              </div>

              {/* Score Breakdown */}
              {lendingScore.score_breakdown && (
                <div className="grid grid-cols-5 gap-2 text-xs">
                  {Object.entries(lendingScore.score_breakdown).map(([key, value]) => (
                    <div key={key} className="text-center">
                      <div className="text-muted-foreground capitalize">{key.replace('_', ' ')}</div>
                      <div className="font-medium">{value.toFixed(0)}</div>
                    </div>
                  ))}
                </div>
              )}

              {/* Summary */}
              <p className="text-sm text-muted-foreground">{lendingScore.summary}</p>

              {/* Matched Programs */}
              {lendingScore.matched_programs?.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-green-600 mb-2">
                    <CheckCircle className="w-4 h-4 inline mr-1" />
                    Qualifies for {lendingScore.matched_programs.length} program(s)
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {lendingScore.matched_programs.map(p => (
                      <Badge key={p.program_id} variant="secondary" className="text-xs">
                        {p.program_name}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Recommendations */}
              {lendingScore.recommendations?.length > 0 && (
                <div className="text-sm text-muted-foreground">
                  <p className="font-medium mb-1">Recommendations:</p>
                  <ul className="list-disc list-inside space-y-1">
                    {lendingScore.recommendations.map((r, i) => (
                      <li key={i}>{r}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ) : (
            <p className="text-muted-foreground text-sm">Add loan details to calculate score</p>
          )}
        </CardContent>
      </Card>

      {/* Loans Card */}
      <Card data-testid="client-loans-card">
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle className="text-lg">Loans</CardTitle>
              <CardDescription>Client's loan applications</CardDescription>
            </div>
            <Button size="sm" onClick={openCreateDialog} data-testid="add-loan-btn">
              <Plus className="w-4 h-4 mr-1" />
              Add Loan
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-muted-foreground">Loading...</p>
          ) : loans.length === 0 ? (
            <p className="text-muted-foreground text-sm">No loans added yet</p>
          ) : (
            <Accordion type="single" collapsible className="w-full">
              {loans.map(loan => (
                <AccordionItem key={loan.loan_id} value={loan.loan_id}>
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center gap-3 text-left">
                      <Home className="w-4 h-4 text-muted-foreground" />
                      <div>
                        <div className="font-medium">{loan.loan_type}</div>
                        <div className="text-sm text-muted-foreground">
                          {loan.loan_amount ? `$${loan.loan_amount.toLocaleString()}` : 'Amount TBD'}
                          {loan.property_type && ` • ${loan.property_type}`}
                        </div>
                      </div>
                      {loan.lending_score && (
                        <Badge className={getScoreBg(loan.lending_score)}>
                          {loan.lending_score.toFixed(0)}%
                        </Badge>
                      )}
                      <Badge variant={loan.status === 'active' ? 'default' : 'secondary'}>
                        {loan.status}
                      </Badge>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-4 pt-2">
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-muted-foreground">Property Address:</span>
                          <p>{loan.property_address || 'Not specified'}</p>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Purchase Price:</span>
                          <p>{loan.purchase_price ? `$${loan.purchase_price.toLocaleString()}` : 'Not specified'}</p>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Down Payment:</span>
                          <p>
                            {loan.down_payment_percent ? `${loan.down_payment_percent}%` : ''}
                            {loan.down_payment_amount ? ` ($${loan.down_payment_amount.toLocaleString()})` : ''}
                            {!loan.down_payment_percent && !loan.down_payment_amount && 'Not specified'}
                          </p>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Est. Monthly Rent:</span>
                          <p>{loan.estimated_rent ? `$${loan.estimated_rent.toLocaleString()}` : 'Not specified'}</p>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Credit Score:</span>
                          <p>{loan.credit_score || 'Not specified'}</p>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Citizenship:</span>
                          <p>{loan.citizenship || 'Not specified'}</p>
                        </div>
                      </div>

                      {loan.notes && (
                        <div className="text-sm">
                          <span className="text-muted-foreground">Notes:</span>
                          <p>{loan.notes}</p>
                        </div>
                      )}

                      {/* Matched Programs */}
                      {loan.matched_programs?.length > 0 && (
                        <div className="text-sm">
                          <span className="text-muted-foreground">Matched Programs:</span>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {loan.matched_programs.map(pid => (
                              <Badge key={pid} variant="outline" className="text-xs">{pid}</Badge>
                            ))}
                          </div>
                        </div>
                      )}

                      <div className="flex gap-2 pt-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleMatchPrograms(loan.loan_id)}
                          disabled={matchingLoanId === loan.loan_id}
                          data-testid={`match-programs-${loan.loan_id}`}
                        >
                          <TrendingUp className="w-4 h-4 mr-1" />
                          {matchingLoanId === loan.loan_id ? 'Matching...' : 'Match Programs'}
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => openEditDialog(loan)}>
                          <Edit className="w-4 h-4 mr-1" />
                          Edit
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleDelete(loan.loan_id)}>
                          <Trash2 className="w-4 h-4 mr-1 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Loan Dialog */}
      <Dialog open={showLoanDialog} onOpenChange={setShowLoanDialog}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingLoan ? 'Edit Loan' : 'Add New Loan'}</DialogTitle>
            <DialogDescription>Enter the loan details for this client</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Loan Type *</Label>
                <Select value={loanForm.loan_type} onValueChange={(v) => setLoanForm({...loanForm, loan_type: v})}>
                  <SelectTrigger data-testid="loan-type-select">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {LOAN_TYPES.map(t => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Status</Label>
                <Select value={loanForm.status} onValueChange={(v) => setLoanForm({...loanForm, status: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {LOAN_STATUSES.map(s => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Loan Amount ($)</Label>
                <Input
                  type="number"
                  value={loanForm.loan_amount}
                  onChange={(e) => setLoanForm({...loanForm, loan_amount: e.target.value})}
                  placeholder="500000"
                />
              </div>
              <div>
                <Label>Purchase Price ($)</Label>
                <Input
                  type="number"
                  value={loanForm.purchase_price}
                  onChange={(e) => setLoanForm({...loanForm, purchase_price: e.target.value})}
                  placeholder="650000"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Down Payment (%)</Label>
                <Input
                  type="number"
                  value={loanForm.down_payment_percent}
                  onChange={(e) => setLoanForm({...loanForm, down_payment_percent: e.target.value})}
                  placeholder="20"
                />
              </div>
              <div>
                <Label>Down Payment ($)</Label>
                <Input
                  type="number"
                  value={loanForm.down_payment_amount}
                  onChange={(e) => setLoanForm({...loanForm, down_payment_amount: e.target.value})}
                  placeholder="130000"
                />
              </div>
            </div>

            <div>
              <Label>Property Address</Label>
              <Input
                value={loanForm.property_address}
                onChange={(e) => setLoanForm({...loanForm, property_address: e.target.value})}
                placeholder="123 Main St, City, State"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Property Type</Label>
                <Select value={loanForm.property_type} onValueChange={(v) => setLoanForm({...loanForm, property_type: v})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {PROPERTY_TYPES.map(t => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Est. Monthly Rent ($)</Label>
                <Input
                  type="number"
                  value={loanForm.estimated_rent}
                  onChange={(e) => setLoanForm({...loanForm, estimated_rent: e.target.value})}
                  placeholder="3500"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Credit Score</Label>
                <Input
                  type="number"
                  value={loanForm.credit_score}
                  onChange={(e) => setLoanForm({...loanForm, credit_score: e.target.value})}
                  placeholder="720"
                />
              </div>
              <div>
                <Label>Citizenship</Label>
                <Select value={loanForm.citizenship} onValueChange={(v) => setLoanForm({...loanForm, citizenship: v})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    {CITIZENSHIP_TYPES.map(t => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Timeline</Label>
              <Input
                value={loanForm.timeline}
                onChange={(e) => setLoanForm({...loanForm, timeline: e.target.value})}
                placeholder="ASAP, 30 days, 60 days..."
              />
            </div>

            <div>
              <Label>Notes</Label>
              <Textarea
                value={loanForm.notes}
                onChange={(e) => setLoanForm({...loanForm, notes: e.target.value})}
                placeholder="Additional notes..."
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLoanDialog(false)}>Cancel</Button>
            <Button onClick={handleSubmit} data-testid="save-loan-btn">
              {editingLoan ? 'Update Loan' : 'Add Loan'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Program Match Results Dialog */}
      <Dialog open={showMatchDialog} onOpenChange={setShowMatchDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Program Match Results</DialogTitle>
            <DialogDescription>
              Lending Score: <span className={`font-bold ${getScoreColor(matchResults?.lending_score || 0)}`}>
                {matchResults?.lending_score?.toFixed(0)}%
              </span>
            </DialogDescription>
          </DialogHeader>

          {matchResults && (
            <div className="space-y-6 py-4">
              {/* Qualified Programs */}
              {matchResults.matched_programs?.length > 0 && (
                <div>
                  <h4 className="font-medium text-green-600 flex items-center gap-2 mb-3">
                    <CheckCircle className="w-4 h-4" />
                    Qualified Programs ({matchResults.matched_programs.length})
                  </h4>
                  <div className="space-y-2">
                    {matchResults.matched_programs.map(p => (
                      <div key={p.program_id} className="p-3 bg-green-50 rounded-lg border border-green-200">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-medium">{p.program_name}</p>
                            <p className="text-sm text-muted-foreground">{p.lender_name}</p>
                          </div>
                          <Badge variant="default" className="bg-green-600">{p.match_percent}% match</Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Partial Matches */}
              {matchResults.partial_matches?.length > 0 && (
                <div>
                  <h4 className="font-medium text-yellow-600 flex items-center gap-2 mb-3">
                    <AlertCircle className="w-4 h-4" />
                    Partial Matches ({matchResults.partial_matches.length})
                  </h4>
                  <div className="space-y-2">
                    {matchResults.partial_matches.map(p => (
                      <div key={p.program_id} className="p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-medium">{p.program_name}</p>
                            <p className="text-sm text-muted-foreground">{p.lender_name}</p>
                            {p.disqualifiers?.length > 0 && (
                              <ul className="text-xs text-red-600 mt-1">
                                {p.disqualifiers.map((d, i) => <li key={i}>• {d}</li>)}
                              </ul>
                            )}
                          </div>
                          <Badge variant="secondary">{p.match_percent}% match</Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* No Matches */}
              {matchResults.matched_programs?.length === 0 && matchResults.partial_matches?.length === 0 && (
                <div className="text-center py-6">
                  <XCircle className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
                  <p className="text-muted-foreground">No matching programs found</p>
                </div>
              )}

              {/* Recommendations */}
              {matchResults.recommendations?.length > 0 && (
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <h4 className="font-medium mb-2">Recommendations</h4>
                  <ul className="text-sm space-y-1">
                    {matchResults.recommendations.map((r, i) => (
                      <li key={i}>• {r}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button onClick={() => setShowMatchDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientLoansSection;
